import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, LogOut, X as XIcon } from "lucide-react";
import { API, BACKEND_URL } from "@/config";
import { ReplacementPicker } from "@/components/customer/ReplacementPicker";
import { useAuth } from "@/context/AuthProvider";
import { formatSlot } from "@/utils/slotLabel";

const RefundBadge = ({ pct }) => (
  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${pct === 100 ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" : pct === 50 ? "border-amber-500/50 text-amber-300 bg-amber-500/10" : "border-red-500/50 text-red-300 bg-red-500/10"}`}>
    {pct}% refund
  </span>
);

const CancelModal = ({ open, onClose, reservation, onConfirmed }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !reservation) return;
    axios.get(`${API}/my/reservations/${reservation.id}`).then(r => setPreview(r.data.refund_preview));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reservation]);

  if (!open || !reservation) return null;
  const isPaid = reservation.payment_status === "paid";

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/my/reservations/${reservation.id}/cancel`);
      const rf = data.refund;
      toast.success(rf ? `Cancelled — ${rf.policy_label}${rf.refund_amount > 0 ? ` ($${rf.refund_amount.toFixed(2)})` : ""}` : "Reservation cancelled");
      onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="cancel-modal">
      <div className="glass max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#B9C8D5] hover:text-[#FFD166]" aria-label="Close" data-testid="cancel-modal-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">Cancel reservation</p>
        <h3 className="font-serif text-2xl mt-1 text-[#F6F1E4]">Reservation #{reservation.id.slice(0, 8)}</h3>
        <p className="text-sm text-[#B9C8D5] mt-3">
          {reservation.delivery_date} → {reservation.pickup_date} · ${reservation.total?.toFixed(2)} total
        </p>
        {isPaid && preview && (
          <div className={`mt-5 p-4 rounded-xl border ${preview.refund_percent === 100 ? "border-emerald-500/40 bg-emerald-500/5" : preview.refund_percent === 50 ? "border-amber-500/40 bg-amber-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="refund-preview">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#F6F1E4]">{preview.policy_label}</span>
              <RefundBadge pct={preview.refund_percent} />
            </div>

            {(() => {
              const total = Number(reservation.total || 0);
              const tax = Number(reservation.tax || 0);
              const tip = Number(reservation.tip_amount || 0);
              const care = Number(reservation.enchanted_care_fee || 0);
              const refund = Number(preview.refund_amount || 0);
              const tipKept = Number(preview.tip_kept || 0);
              const careKept = Number(preview.care_kept || 0);
              // "Non-refundable cancel fee" = everything we retain that is
              // NOT already broken out as tip-kept or care-kept. This bucket
              // covers the PoM premium (20 %), the standard-tier retention
              // (50 % / 100 %), and any other future add-on fee we choose to
              // keep. It equals total - refund - tipKept - careKept.
              const cancelFee = Math.max(
                0,
                Math.round((total - refund - tipKept - careKept) * 100) / 100
              );
              // Stripe keeps its processing fee on every refund (~2.9 % + $0.30).
              // We compute it against what actually gets refunded so the
              // customer sees the true dollar amount that lands on their card.
              const stripeFee = refund > 0 ? Math.round((refund * 0.029 + 0.30) * 100) / 100 : 0;
              const netToCustomer = Math.max(0, Math.round((refund - stripeFee) * 100) / 100);
              return (
                <div className="mt-3 space-y-3 text-xs" data-testid="refund-breakdown-detail">
                  {/* What you paid — pure informational breakout of the charge */}
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70">What you paid</div>
                    <div className="flex justify-between text-[#F6F1E4]">
                      <span>Subtotal</span><span data-testid="refund-total-paid">${total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#B9C8D5]">
                      <span>Sales tax</span><span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#B9C8D5]">
                      <span>Tip</span><span>${tip.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Deductions — non-refundable pieces + Stripe fee */}
                  <div className="space-y-1 border-t border-[#FFD166]/15 pt-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70">Deductions</div>
                    <div className="flex justify-between text-red-300">
                      <span>Non-refundable cancel fee</span>
                      <span data-testid="refund-cancel-fee">−${cancelFee.toFixed(2)}</span>
                    </div>
                    {tipKept > 0 && (
                      <div className="flex justify-between text-red-300">
                        <span>Tip kept (reservation started)</span>
                        <span>−${tipKept.toFixed(2)}</span>
                      </div>
                    )}
                    {careKept > 0 && (
                      <div className="flex justify-between text-red-300">
                        <span>Enchanted Care fee (kept)</span>
                        <span>−${careKept.toFixed(2)}</span>
                      </div>
                    )}
                    {refund > 0 && (
                      <div className="flex justify-between text-red-300">
                        <span>Card processing fee (2.9% + $0.30)</span>
                        <span data-testid="refund-stripe-fee">−${stripeFee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Final net-to-customer */}
                  {refund > 0 ? (
                    <>
                      <div className="flex justify-between border-t border-[#FFD166]/30 pt-2 text-emerald-300 font-medium text-sm">
                        <span>Total refund to your card</span>
                        <span data-testid="refund-net-to-customer">${netToCustomer.toFixed(2)}</span>
                      </div>
                      <p className="text-[10px] text-[#B9C8D5]">
                        {preview.policy === "pom" ? (
                          <>Instant refund thanks to Enchanted Peace of Mind ✨ — Stripe usually settles within 5-10 business days. Stripe's processing fees are non-refundable per their policy.</>
                        ) : (
                          <>Refund goes to our team for review first — we'll process it to your original payment method within 5 business days once approved. Stripe's processing fees are non-refundable per their policy.</>
                        )}
                      </p>
                    </>
                  ) : (
                    <div className="border-t border-[#FFD166]/30 pt-2">
                      <div className="flex justify-between text-red-300 font-medium text-sm">
                        <span>Total refund to your card</span>
                        <span data-testid="refund-net-to-customer">$0.00</span>
                      </div>
                      <p className="text-[10px] text-[#B9C8D5] mt-2">
                        No refund available on this cancellation tier.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {!isPaid && (
          <div className="mt-5 p-4 rounded-xl border border-[#FFD166]/25 bg-[#FFD166]/5 text-sm text-[#B9C8D5]">
            This booking was never paid — cancelling won't charge or refund anything.
          </div>
        )}
        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="cancel-back">Keep it</button>
          <button onClick={confirmCancel} disabled={busy} className="flex-1 py-2.5 rounded-full bg-red-500/80 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-60" data-testid="cancel-confirm">
            {busy ? "…" : "Cancel reservation"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditDatesModal = ({ open, onClose, reservation, onSaved }) => {
  const [d1, setD1] = useState("");
  const [d2, setD2] = useState("");
  const [step, setStep] = useState("form"); // "form" → "review"
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [pickedCard, setPickedCard] = useState(""); // "" | pm_id | "email_link"
  const [replacements, setReplacements] = useState([]);
  useEffect(() => {
    if (open && reservation) {
      setD1(reservation.delivery_date || "");
      setD2(reservation.pickup_date || "");
      setStep("form");
      setPreview(null);
      setPickedCard("");
      // Prefetch saved cards so the review step shows them instantly.
      axios.get(`${API}/my/saved-cards`)
        .then((r) => {
          const cards = r.data?.cards || [];
          setSavedCards(cards);
          const def = cards.find((c) => c.is_default) || cards[0];
          if (def) setPickedCard(def.id);
          else setPickedCard("email_link");
        })
        .catch(() => { setSavedCards([]); setPickedCard("email_link"); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reservation]);
  if (!open || !reservation) return null;
  const today = new Date().toISOString().slice(0, 10);
  const goReview = async (reps = replacements) => {
    if (!d1 || !d2) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/my/reservations/${reservation.id}/preview-dates`, { delivery_date: d1, pickup_date: d2, replacements: reps });
      setPreview(data);
      setStep("review");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't build preview.");
    } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { delivery_date: d1, pickup_date: d2, replacements };
      if (isCharge && pickedCard && pickedCard !== "email_link") {
        body.payment_method_id = pickedCard;
      }
      await axios.patch(`${API}/my/reservations/${reservation.id}/dates`, body);
      toast.success("Change requested ✨ Owner will review shortly.");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't submit change request");
    } finally { setBusy(false); }
  };
  const delta = Number(preview?.delta || 0);
  const isCharge = delta > 0.005;
  const isRefund = delta < -0.005;
  return (
    <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="edit-dates-modal">
      <div className="glass max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#B9C8D5] hover:text-[#FFD166]" aria-label="Close" data-testid="edit-dates-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">{step === "form" ? "Request date change" : "Review changes"}</p>
        <h3 className="font-serif text-2xl mt-1 text-[#F6F1E4]">Reservation #{reservation.id.slice(0, 8).toUpperCase()}</h3>

        {step === "form" && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-2">Pick any new dates — we'll show you the price change before you submit. Nothing changes until the owner approves.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div>
                <label className="eyebrow block mb-1 text-[#B9C8D5]">Delivery</label>
                <input type="date" min={today} value={d1} onChange={(e) => setD1(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" data-testid="edit-dates-delivery" />
              </div>
              <div>
                <label className="eyebrow block mb-1 text-[#B9C8D5]">Pickup</label>
                <input type="date" min={d1 || today} value={d2} onChange={(e) => setD2(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" data-testid="edit-dates-pickup" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="edit-dates-cancel">Cancel</button>
              <button onClick={goReview} disabled={busy || !d1 || !d2 || (d1 === reservation.delivery_date && d2 === reservation.pickup_date)} className="flex-1 py-2.5 rounded-full bg-[#FFD166] text-[#040A0F] text-sm font-medium disabled:opacity-60" data-testid="edit-dates-next">{busy ? "…" : "Review changes"}</button>
            </div>
          </>
        )}

        {step === "review" && preview && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-2">Here's what will change. Confirm below to send it to the owner for approval.</p>
            <div className="mt-5 rounded-xl border border-[#FFD166]/20 bg-black/30 p-4 space-y-3 text-sm" data-testid="edit-dates-review">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70 mb-1">Dates</div>
                <div className="flex justify-between text-[#B9C8D5]"><span>Old</span><span data-testid="edit-dates-review-old">{preview.old_delivery_date} → {preview.old_pickup_date}</span></div>
                <div className="flex justify-between text-[#F6F1E4]"><span>New</span><span data-testid="edit-dates-review-new">{preview.new_delivery_date} → {preview.new_pickup_date} · {preview.new_days} day{preview.new_days === 1 ? "" : "s"}</span></div>
              </div>
              <div className="border-t border-[#FFD166]/15 pt-3">
                <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70 mb-1">Total</div>
                <div className="flex justify-between text-[#B9C8D5]"><span>Old total</span><span>${preview.old_total.toFixed(2)}</span></div>
                <div className="flex justify-between text-[#F6F1E4]"><span>New total</span><span>${preview.new_total.toFixed(2)}</span></div>
              </div>
              {isCharge && (
                <div className="border-t border-[#FFD166]/15 pt-3 flex justify-between text-amber-300 font-medium text-base">
                  <span>Extra charge</span>
                  <span data-testid="edit-dates-delta">+${Math.abs(delta).toFixed(2)}</span>
                </div>
              )}
              {isRefund && (
                <div className="border-t border-[#FFD166]/15 pt-3 flex justify-between text-emerald-300 font-medium text-base">
                  <span>Potential refund</span>
                  <span data-testid="edit-dates-delta">−${Math.abs(delta).toFixed(2)}</span>
                </div>
              )}
              {!isCharge && !isRefund && (
                <div className="border-t border-[#FFD166]/15 pt-3 flex justify-between text-[#F6F1E4] font-medium text-base">
                  <span>Price change</span>
                  <span data-testid="edit-dates-delta">$0.00</span>
                </div>
              )}
              {!preview.availability_ok && (
                <div className="border-t border-red-500/30 pt-3" data-testid="edit-dates-availability-warn">
                  <ReplacementPicker preview={preview} replacements={replacements} testidPrefix="edit-dates-replacement" onPick={(from, to) => { const next = [...replacements.filter((r) => r.from_product_id !== from), { from_product_id: from, to_product_id: to }]; setReplacements(next); goReview(next); }} />
                </div>
              )}
              {preview.replacements?.length > 0 && preview.availability_ok && (
                <div className="border-t border-emerald-500/30 pt-3 text-xs text-emerald-300" data-testid="edit-dates-replacement-applied">✓ Replacement item selected — the price difference above includes the swap.</div>
              )}
              <p className="text-[10px] text-[#B9C8D5] pt-2 border-t border-[#FFD166]/15">
                {isCharge
                  ? (pickedCard && pickedCard !== "email_link"
                      ? "If approved, we'll auto-charge the card you picked below. If it declines, we'll email you a secure Stripe link to complete the extra charge."
                      : "If approved, we'll email you a secure Stripe payment link to complete the extra charge before the change is applied.")
                  : isRefund
                    ? "If approved, the refund will be sent to your original payment method within 5 business days."
                    : "No price change on this reservation."}
              </p>
            </div>
            {isCharge && (
              <div className="mt-4 rounded-xl border border-[#FFD166]/20 bg-black/30 p-4" data-testid="edit-dates-card-picker">
                <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70 mb-2">
                  Card to charge on approval
                </div>
                {savedCards.length > 0 && (
                  <div className="space-y-2">
                    {savedCards.map((c) => (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${pickedCard === c.id ? "border-[#FFD166]/60 bg-[#FFD166]/8" : "border-[#FFD166]/15 hover:border-[#FFD166]/40"}`}
                        data-testid={`edit-dates-card-${c.id}`}
                      >
                        <input
                          type="radio"
                          name="edit-dates-card"
                          value={c.id}
                          checked={pickedCard === c.id}
                          onChange={() => setPickedCard(c.id)}
                          className="accent-[#FFD166]"
                        />
                        <span className="text-sm text-[#F6F1E4] flex-1">
                          {(c.brand || "card").toUpperCase()} · <span className="tracking-widest text-[#B9C8D5]">•••• {c.last4}</span>
                          {c.exp_month && c.exp_year && (
                            <span className="text-[10px] text-[#B9C8D5] ml-2">exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
                          )}
                        </span>
                        {c.is_default && <span className="text-[10px] uppercase tracking-widest text-[#FFD166]">Default</span>}
                      </label>
                    ))}
                  </div>
                )}
                <label
                  className={`mt-2 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${pickedCard === "email_link" ? "border-[#FFD166]/60 bg-[#FFD166]/8" : "border-[#FFD166]/15 hover:border-[#FFD166]/40"}`}
                  data-testid="edit-dates-card-email-link"
                >
                  <input
                    type="radio"
                    name="edit-dates-card"
                    value="email_link"
                    checked={pickedCard === "email_link"}
                    onChange={() => setPickedCard("email_link")}
                    className="accent-[#FFD166]"
                  />
                  <span className="text-sm text-[#F6F1E4]">
                    Use a new card <span className="text-[10px] text-[#B9C8D5]">(we'll email you a secure Stripe link if approved)</span>
                  </span>
                </label>
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep("form")} disabled={busy} className="flex-1 py-2.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="edit-dates-back">Back</button>
              <button onClick={save} disabled={busy || (preview && !preview.availability_ok)} className="flex-1 py-2.5 rounded-full bg-[#FFD166] text-[#040A0F] text-sm font-medium disabled:opacity-60" data-testid="edit-dates-confirm">{busy ? "…" : "Confirm & submit"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AddItemsModal = ({ open, onClose, reservation, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [step, setStep] = useState("form"); // "form" → "review"
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [pickedCard, setPickedCard] = useState("");
  useEffect(() => {
    if (open) {
      axios.get(`${API}/products`).then((r) => setProducts(r.data.products || []));
      setCart({});
      setStep("form");
      setQuote(null);
      setPickedCard("");
      axios.get(`${API}/my/saved-cards`)
        .then((r) => {
          const cards = r.data?.cards || [];
          setSavedCards(cards);
          const def = cards.find((c) => c.is_default) || cards[0];
          setPickedCard(def ? def.id : "new_card");
        })
        .catch(() => { setSavedCards([]); setPickedCard("new_card"); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open || !reservation) return null;
  const items = Object.entries(cart).filter(([, q]) => q > 0);
  const roughTotal = items.reduce((sum, [pid, q]) => {
    const p = products.find((x) => x.id === pid); if (!p) return sum;
    const days = Math.max(1, Math.floor((new Date(reservation.pickup_date) - new Date(reservation.delivery_date)) / 86400000) + 1);
    const rate = days <= 3 ? p.tier_1_3 : days <= 7 ? p.tier_4_7 : p.tier_8_plus;
    return sum + rate * days * q;
  }, 0);
  const goReview = async () => {
    if (items.length === 0) return toast.error("Pick at least one item to add.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/quote`, {
        start_date: reservation.delivery_date,
        end_date: reservation.pickup_date,
        items: items.map(([product_id, quantity]) => ({ product_id, quantity })),
        zone_id: null,
      });
      setQuote(data);
      setStep("review");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Couldn't build preview.");
    } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const body = { items: items.map(([product_id, quantity]) => ({ product_id, quantity })) };
      if (pickedCard && pickedCard !== "new_card") {
        body.payment_method_id = pickedCard;
      }
      const { data } = await axios.post(`${API}/my/reservations/${reservation.id}/add-items`, body);
      // If the backend charged the saved card, no Stripe redirect.
      if (data?.charged && data?.method === "saved_card") {
        const label = data.card_brand ? `${data.card_brand.toUpperCase()} ••••${data.card_last4 || "????"}` : "saved card";
        toast.success(`Charged ${label} for $${Number(data.amount || 0).toFixed(2)} ✨ Items added.`);
        setBusy(false);
        onClose?.();
        onSaved?.();
        return;
      }
      // Otherwise redirect to Stripe.
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Something went wrong — please try again.");
      setBusy(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't create add-on checkout.");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="add-items-modal">
      <div className="glass max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#B9C8D5] hover:text-[#FFD166]" aria-label="Close" data-testid="add-items-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">{step === "form" ? "Add items to booking" : "Review add-on"}</p>
        <h3 className="font-serif text-2xl mt-1 text-[#F6F1E4]">Reservation #{reservation.id.slice(0, 8).toUpperCase()}</h3>

        {step === "form" && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-2">You'll be charged for these extras separately on Stripe. Availability is checked against your rental dates.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto pr-1" data-testid="add-items-catalog">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 border border-[#FFD166]/15 rounded-lg" data-testid={`add-item-${p.id}`}>
                  <img src={p.image ? (p.image.startsWith("http") ? p.image : `${BACKEND_URL}${p.image.startsWith("/api") ? p.image : "/api" + p.image}`) : ""} alt="" className="w-12 h-12 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F6F1E4] truncate">{p.name}</div>
                    <div className="text-xs text-[#B9C8D5]">${p.price_per_day}/day</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCart((c) => ({ ...c, [p.id]: Math.max(0, (c[p.id] || 0) - 1) }))} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`add-item-${p.id}-minus`}>−</button>
                    <span className="w-6 text-center text-sm text-[#F6F1E4]">{cart[p.id] || 0}</span>
                    <button onClick={() => setCart((c) => ({ ...c, [p.id]: (c[p.id] || 0) + 1 }))} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`add-item-${p.id}-plus`}>+</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <div>
                <div className="eyebrow text-[#FFD166]">Estimated add-on</div>
                <div className="font-serif text-3xl text-[#F6F1E4]" data-testid="add-items-estimate">${roughTotal.toFixed(2)}</div>
              </div>
              <button onClick={goReview} disabled={busy || items.length === 0} className="py-3 px-6 rounded-full bg-gradient-to-r from-[#D97B37] to-[#F4A93A] text-[#1a0a03] font-medium disabled:opacity-60" data-testid="add-items-next">
                {busy ? "…" : "Review add-on"}
              </button>
            </div>
          </>
        )}

        {step === "review" && quote && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-2">Confirm what you're adding — you'll be redirected to Stripe to pay this amount only.</p>
            <div className="mt-5 rounded-xl border border-[#FFD166]/20 bg-black/30 p-5 space-y-3 text-sm" data-testid="add-items-review">
              <ul className="space-y-2" data-testid="add-items-review-list">
                {(quote.line_items || []).map((li) => (
                  <li key={li.product_id} className="flex items-center justify-between">
                    <div>
                      <div className="text-[#F6F1E4]">{li.name}</div>
                      <div className="text-[11px] text-[#B9C8D5]">× {li.quantity} · ${Number(li.unit_rate_per_day || 0).toFixed(2)}/day × {li.days}d</div>
                    </div>
                    <div className="text-[#FFD166] font-medium">${Number(li.subtotal || 0).toFixed(2)}</div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-[#FFD166]/15 pt-3 space-y-1">
                <div className="flex justify-between text-[#B9C8D5]"><span>Subtotal</span><span>${Number(quote.subtotal || 0).toFixed(2)}</span></div>
                {Number(quote.tax || 0) > 0 && <div className="flex justify-between text-[#B9C8D5]"><span>Sales tax</span><span>${Number(quote.tax).toFixed(2)}</span></div>}
              </div>
              <div className="border-t border-[#FFD166]/25 pt-3 flex justify-between text-emerald-300 font-medium text-base">
                <span>Total charge</span>
                <span data-testid="add-items-review-total">${Number(quote.subtotal || 0).toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-[#B9C8D5]">
                Delivery is already covered by your existing trip — no extra delivery fee.
              </p>
            </div>
            <div className="mt-4 rounded-xl border border-[#FFD166]/20 bg-black/30 p-4" data-testid="add-items-card-picker">
              <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70 mb-2">
                Card to charge
              </div>
              {savedCards.length > 0 && (
                <div className="space-y-2">
                  {savedCards.map((c) => (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${pickedCard === c.id ? "border-[#FFD166]/60 bg-[#FFD166]/8" : "border-[#FFD166]/15 hover:border-[#FFD166]/40"}`}
                      data-testid={`add-items-card-${c.id}`}
                    >
                      <input type="radio" name="add-items-card" value={c.id} checked={pickedCard === c.id} onChange={() => setPickedCard(c.id)} className="accent-[#FFD166]" />
                      <span className="text-sm text-[#F6F1E4] flex-1">
                        {(c.brand || "card").toUpperCase()} · <span className="tracking-widest text-[#B9C8D5]">•••• {c.last4}</span>
                        {c.exp_month && c.exp_year && (
                          <span className="text-[10px] text-[#B9C8D5] ml-2">exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
                        )}
                      </span>
                      {c.is_default && <span className="text-[10px] uppercase tracking-widest text-[#FFD166]">Default</span>}
                    </label>
                  ))}
                </div>
              )}
              <label
                className={`mt-2 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${pickedCard === "new_card" ? "border-[#FFD166]/60 bg-[#FFD166]/8" : "border-[#FFD166]/15 hover:border-[#FFD166]/40"}`}
                data-testid="add-items-card-new"
              >
                <input type="radio" name="add-items-card" value="new_card" checked={pickedCard === "new_card"} onChange={() => setPickedCard("new_card")} className="accent-[#FFD166]" />
                <span className="text-sm text-[#F6F1E4]">
                  Use a new card <span className="text-[10px] text-[#B9C8D5]">(redirects to Stripe)</span>
                </span>
              </label>
            </div>
            <div className="mt-6 flex items-center justify-between gap-3">
              <button onClick={() => setStep("form")} disabled={busy} className="py-2.5 px-6 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="add-items-back">Back</button>
              <button onClick={submit} disabled={busy} className="py-3 px-6 rounded-full bg-gradient-to-r from-[#D97B37] to-[#F4A93A] text-[#1a0a03] font-medium disabled:opacity-60" data-testid="add-items-confirm">
                {busy ? "…" : (pickedCard && pickedCard !== "new_card" ? `Charge ${(savedCards.find((c) => c.id === pickedCard)?.brand || "card").toUpperCase()} ••••${savedCards.find((c) => c.id === pickedCard)?.last4 || ""}` : "Continue to Stripe")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RemoveItemsModal = ({ open, onClose, reservation, onSubmitted }) => {
  const [remove, setRemove] = useState({});
  const [reason, setReason] = useState("");
  const [step, setStep] = useState("form"); // "form" → "review"
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setRemove({}); setReason("");
      setStep("form"); setPreview(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open || !reservation) return null;
  const items = Object.entries(remove).filter(([, q]) => q > 0);
  const goReview = async () => {
    if (items.length === 0) return toast.error("Choose what to remove.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/my/reservations/${reservation.id}/preview-item-removal`, {
        items: items.map(([product_id, quantity]) => ({ product_id, quantity })),
        reason,
      });
      setPreview(data);
      setStep("review");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't build preview.");
    } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/my/reservations/${reservation.id}/request-item-removal`, {
        items: items.map(([product_id, quantity]) => ({ product_id, quantity })),
        reason,
      });
      toast.success("Removal request submitted — admin will review and process any refund.");
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit request.");
    } finally { setBusy(false); }
  };
  const pct = Number(preview?.refund_percent || 0);
  const refund = Number(preview?.refund_amount || 0);
  return (
    <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="remove-items-modal">
      <div className="glass max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#B9C8D5] hover:text-[#FFD166]" aria-label="Close" data-testid="remove-items-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">{step === "form" ? "Remove items" : "Review removal"}</p>
        <h3 className="font-serif text-2xl mt-1 text-[#F6F1E4]">Reservation #{reservation.id.slice(0, 8).toUpperCase()}</h3>

        {step === "form" && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-2">Choose how many of each to drop. We'll show you the refund amount before you submit.</p>
            <div className="mt-5 space-y-3" data-testid="remove-items-list">
              {(reservation.line_items || []).map((li) => (
                <div key={li.product_id} className="flex items-center justify-between border border-[#FFD166]/15 rounded-lg p-3">
                  <div>
                    <div className="text-sm text-[#F6F1E4]">{li.name}</div>
                    <div className="text-xs text-[#B9C8D5]">currently × {li.quantity}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setRemove((c) => ({ ...c, [li.product_id]: Math.max(0, (c[li.product_id] || 0) - 1) }))} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`remove-${li.product_id}-minus`}>−</button>
                    <span className="w-6 text-center text-sm">{remove[li.product_id] || 0}</span>
                    <button onClick={() => setRemove((c) => ({ ...c, [li.product_id]: Math.min(li.quantity, (c[li.product_id] || 0) + 1) }))} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`remove-${li.product_id}-plus`}>+</button>
                  </div>
                </div>
              ))}
            </div>
            <textarea placeholder="Optional note for our team…" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-4 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" rows={2} data-testid="remove-items-reason" />
            <div className="mt-5 flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="remove-items-cancel-btn">Cancel</button>
              <button onClick={goReview} disabled={busy || items.length === 0} className="flex-1 py-2.5 rounded-full bg-[#FFD166] text-[#040A0F] text-sm font-medium disabled:opacity-60" data-testid="remove-items-next">{busy ? "…" : "Review removal"}</button>
            </div>
          </>
        )}

        {step === "review" && preview && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-2">Here's what you're removing and the refund preview. Confirm to send it to the owner for approval.</p>
            <div className="mt-5 rounded-xl border border-[#FFD166]/20 bg-black/30 p-5 space-y-3 text-sm" data-testid="remove-items-review">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/70 mb-2">Items to remove</div>
                <ul className="space-y-1">
                  {(preview.line_items_to_remove || []).map((li) => (
                    <li key={li.product_id} className="flex justify-between text-[#F6F1E4]">
                      <span>× {li.quantity} {li.name}</span>
                      <span className="text-[#FFD166]">${Number(li.subtotal || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-[#FFD166]/15 pt-3 space-y-1">
                <div className="flex justify-between text-[#B9C8D5]">
                  <span>Removed items value</span>
                  <span>${Number(preview.refund_before_policy || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#B9C8D5]">
                  <span>Policy</span>
                  <span data-testid="remove-items-policy"><RefundBadge pct={pct} /></span>
                </div>
                <div className="text-[10px] text-[#B9C8D5] pt-1">{preview.refund_policy_label}</div>
              </div>
              <div className="border-t border-[#FFD166]/25 pt-3 flex justify-between font-medium text-base">
                <span className={refund > 0 ? "text-emerald-300" : "text-red-300"}>Refund preview</span>
                <span className={refund > 0 ? "text-emerald-300" : "text-red-300"} data-testid="remove-items-refund">{refund > 0 ? `$${refund.toFixed(2)}` : "$0.00"}</span>
              </div>
              <p className="text-[10px] text-[#B9C8D5]">
                Our team reviews every removal request before issuing the refund. Once approved, funds return to your original payment method within 5 business days. Stripe's processing fees are non-refundable.
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep("form")} disabled={busy} className="flex-1 py-2.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="remove-items-back">Back</button>
              <button onClick={submit} disabled={busy} className="flex-1 py-2.5 rounded-full bg-[#FFD166] text-[#040A0F] text-sm font-medium disabled:opacity-60" data-testid="remove-items-confirm">{busy ? "…" : "Confirm & submit"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const CustomerAccount = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [editingDates, setEditingDates] = useState(null);
  const [addingItems, setAddingItems] = useState(null);
  const [removingItems, setRemovingItems] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/my/reservations`).then((r) => setRows(r.data.reservations || []));
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user === false) { nav("/login?role=customer", { replace: true }); return; }
    if (user && user.role && user.role !== "customer") {
      const home = { admin: "/admin", employee: "/employee" }[user.role];
      nav(home, { replace: true });
      return;
    }
    if (user && user.role === "customer") load();
  }, [user, nav, load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("add_ok") === "1") {
      toast.success("Extra items paid — they've been added to your reservation ✨");
      window.history.replaceState({}, "", "/account");
    } else if (p.get("add_cancel") === "1") {
      toast("Add-on checkout cancelled — no charge made.");
      window.history.replaceState({}, "", "/account");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const p = new URLSearchParams(window.location.search);
    const targetId = p.get("res");
    if (!targetId) return;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="res-${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#FFD166]", "ring-offset-4", "ring-offset-[#040A0F]");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-[#FFD166]", "ring-offset-4", "ring-offset-[#040A0F]");
        }, 3200);
      }
    });
  }, [rows]);

  if (!user) return <div className="min-h-screen flex items-center justify-center text-[#B9C8D5]">Loading…</div>;

  // Customers can request any change — the owner reviews and approves/denies.
  // The old 7-day self-serve lock is gone; requests are safe because they land
  // in the pending queue instead of applying immediately.
  const canCancel = (r) => r.status !== "cancelled" && r.status !== "completed";
  const groupedUpcoming = (rows || []).filter((r) => r.status !== "cancelled" && (!r.pickup_date || r.pickup_date >= new Date().toISOString().slice(0, 10)));
  const groupedPast = (rows || []).filter((r) => !groupedUpcoming.includes(r));

  const daysUntil = (r) => {
    if (!r.delivery_date) return 999;
    return Math.floor((new Date(r.delivery_date) - new Date()) / (1000 * 60 * 60 * 24));
  };
  const canEdit = (r) => r.payment_status === "paid" && r.status !== "cancelled" && r.status !== "completed" && !r.pending_refund_request && !r.pending_change_request;

  const productImage = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p?.image) return null;
    if (p.image.startsWith("http")) return p.image;
    return `${BACKEND_URL}${p.image.startsWith("/") ? "" : "/"}${p.image}`;
  };

  const Row = ({ r }) => (
    <div className="border border-[#FFD166]/20 rounded-2xl p-5 md:p-6 bg-black/25" data-testid={`res-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#B9C8D5]">#{r.id.slice(0, 8).toUpperCase()}</div>
          <button
            type="button"
            onClick={() => nav(`/account/reservation/${r.id}`)}
            className="font-serif text-2xl text-[#F6F1E4] mt-1 text-left hover:text-[#FFD166] transition-colors"
            data-testid={`res-${r.id}-open`}
          >
            {r.delivery_date} → {r.pickup_date}
          </button>
          <div className="text-sm text-[#B9C8D5] mt-0.5">{r.delivery_resort}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-serif text-[#FFD166]">${r.total?.toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5] mt-1">
            {r.status} · {r.payment_status}
          </div>
          <button
            type="button"
            onClick={() => nav(`/account/reservation/${r.id}`)}
            className="mt-2 text-[10px] uppercase tracking-wider text-[#FFD166] hover:underline"
            data-testid={`res-${r.id}-details`}
          >
            Full details →
          </button>
        </div>
      </div>
      <ul className="mt-4 space-y-2" data-testid={`res-${r.id}-items`}>
        {(r.line_items || []).map((li) => {
          const img = productImage(li.product_id);
          return (
            <li key={li.product_id} className="flex items-center gap-3">
              {img ? (
                <img src={img} alt={li.name} className="w-12 h-12 rounded-lg object-cover border border-[#FFD166]/20" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-black/40 border border-[#FFD166]/20" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#F6F1E4] truncate">{li.name}</div>
                <div className="text-[11px] text-[#B9C8D5]">× {li.quantity} · ${Number(li.unit_rate_per_day || 0).toFixed(2)}/day × {li.days}d</div>
              </div>
              <div className="text-sm text-[#FFD166] font-medium">${Number(li.subtotal || 0).toFixed(2)}</div>
            </li>
          );
        })}
      </ul>

      {/* Full breakdown — collapsible */}
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
          className="text-xs text-[#FFD166] hover:underline"
          data-testid={`res-${r.id}-expand`}
        >
          {expandedId === r.id ? "▾ Hide full breakdown" : "▸ Show full breakdown"}
        </button>
        {expandedId === r.id && (
          <div className="mt-3 rounded-xl border border-[#FFD166]/20 bg-black/30 p-4 text-sm space-y-1" data-testid={`res-${r.id}-breakdown`}>
            <div className="flex justify-between text-[#B9C8D5]"><span>Subtotal ({r.days || 0} days)</span><span>${Number(r.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-[#B9C8D5]"><span>Delivery</span><span>{Number(r.delivery_fee || 0) === 0 ? "FREE" : `$${Number(r.delivery_fee).toFixed(2)}`}</span></div>
            <div className="flex justify-between text-[#B9C8D5]"><span>Sales tax</span><span>${Number(r.tax || 0).toFixed(2)}</span></div>
            {Number(r.enchanted_care_fee || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>Enchanted Care</span><span>+${Number(r.enchanted_care_fee).toFixed(2)}</span></div>}
            {Number(r.enchanted_pom_fee || 0) > 0 && <div className="flex justify-between text-[#FFD166]"><span>Enchanted Peace of Mind</span><span>+${Number(r.enchanted_pom_fee).toFixed(2)}</span></div>}
            {Number(r.damage_protection_fee || 0) > 0 && <div className="flex justify-between text-[#B9C8D5]"><span>Damage Protection</span><span>+${Number(r.damage_protection_fee).toFixed(2)}</span></div>}
            {Number(r.last_minute_fee || 0) > 0 && <div className="flex justify-between text-[#B9C8D5]"><span>Last-minute fee</span><span>+${Number(r.last_minute_fee).toFixed(2)}</span></div>}
            {Number(r.promo_discount || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>{r.partner_code ? `Partner discount (${r.partner_code})` : `Firefly Wishes (${r.promo_code})`}</span><span>−${Number(r.promo_discount).toFixed(2)}</span></div>}
            <div className="flex justify-between text-emerald-300"><span>Tip (not taxed)</span><span>+${Number(r.tip_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-[#FFD166]/20 pt-2 mt-2 text-[#FFD166] font-medium">
              <span>Total paid</span><span>${Number(r.total || 0).toFixed(2)}</span>
            </div>
            <div className="mt-2 text-[10px] text-[#B9C8D5]">Delivered {formatSlot(r.delivery_slot)} · Picked up {formatSlot(r.pickup_slot)}</div>
          </div>
        )}
      </div>

      {r.pending_change_request && (
        <div className="mt-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 text-xs text-amber-200" data-testid={`res-${r.id}-pending-change`}>
          <strong>Date change requested:</strong> {r.pending_change_request.new_delivery_date} → {r.pending_change_request.new_pickup_date}
          {" · "}Waiting for owner approval ✨
          {r.pending_change_request.payment_status === "awaiting_payment" && (
            <div className="mt-1 text-amber-300">Payment required — check your email for the payment link.</div>
          )}
        </div>
      )}
      {r.last_change_denied_at && !r.pending_change_request && (
        <div className="mt-4 p-3 rounded-lg border border-red-500/30 bg-red-500/5 text-xs text-red-200" data-testid={`res-${r.id}-change-denied`}>
          <strong>Date change request was declined.</strong>
          {r.last_change_denied_note && (
            <div className="mt-1 text-red-100/80">Reason: {r.last_change_denied_note}</div>
          )}
          <div className="mt-1 text-[10px] text-red-200/60">
            {new Date(r.last_change_denied_at).toLocaleDateString()} · Your original booking dates are unchanged.
          </div>
        </div>
      )}
      {r.pending_refund_request && (
        <div className="mt-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 text-xs text-amber-200" data-testid={`res-${r.id}-pending`}>
          <strong>Pending admin review:</strong> {r.pending_refund_request.type === "item_removal" ? `Item removal (${r.pending_refund_request.line_items_to_remove?.map(l => `× ${l.quantity} ${l.name}`).join(", ")})` : "Change request"} · Refund preview ${r.pending_refund_request.refund_amount?.toFixed(2)} ({r.pending_refund_request.refund_percent}%)
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        {r.payment_status === "pending" && (
          <button onClick={() => nav("/")} className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10" data-testid={`res-${r.id}-finish`}>
            Finish booking
          </button>
        )}
        {canEdit(r) && (
          <>
            <button onClick={() => setEditingDates(r)} className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10" data-testid={`res-${r.id}-edit-dates`}>
              Edit dates
            </button>
            <button onClick={() => setAddingItems(r)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`res-${r.id}-add-items`}>
              Add items
            </button>
            <button onClick={() => setRemovingItems(r)} className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4]/90 hover:bg-[#FFD166]/10" data-testid={`res-${r.id}-remove-items`}>
              Remove items
            </button>
          </>
        )}
        {/* "Within 7 days — message us" pill intentionally removed. Customers
            find it noisy on an already-busy card. Cancel button also hides
            inside the 7-day lock (see canCancel). */}
        {canCancel(r) && (
          <button onClick={() => setCancelling(r)} className="text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`res-${r.id}-cancel`}>
            Cancel
          </button>
        )}
        {r.status === "cancelled" && r.refund_amount > 0 && r.refund_status !== "processed" && r.refund_status !== "pending_stripe" && r.refund_status !== "refund_failed" && (
          <span className="text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 bg-amber-500/10">
            Refund pending admin · ${r.refund_amount.toFixed(2)}
          </span>
        )}
        {r.refund_status === "pending_stripe" && (
          <span
            className="text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-200 bg-amber-500/10"
            title="Stripe has accepted the refund. It usually appears on your card in 5–10 business days."
            data-testid={`res-${r.id}-refund-pending-stripe`}
          >
            Refund on the way · ${((r.stripe_refund?.amount || 0) / 100).toFixed(2)} <span className="opacity-70">· 5–10 business days</span>
          </span>
        )}
        {r.refund_status === "refund_failed" && (
          <span
            className="text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 bg-red-500/10"
            title={r.refund_failure_reason || "Contact us to complete your refund."}
            data-testid={`res-${r.id}-refund-failed`}
          >
            Refund failed · please contact us
          </span>
        )}
        {r.refund_status === "processed" && r.stripe_refund && (
          <span className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/10" data-testid={`res-${r.id}-refunded`}>
            Refunded ${(r.stripe_refund.amount / 100).toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden py-16 px-4">
      <div className="container max-w-4xl relative z-10">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => nav("/")} className="text-[#B9C8D5] hover:text-[#FFD166] text-sm flex items-center gap-1" data-testid="account-back"><ArrowLeft className="w-4 h-4" /> Back to site</button>
          <div className="flex items-center gap-4">
            <button onClick={() => nav("/account/cards")} className="text-[#B9C8D5] hover:text-[#FFD166] text-sm flex items-center gap-1" data-testid="account-manage-cards"><CreditCard className="w-4 h-4" /> Saved cards</button>
            <button onClick={async () => { await logout(); nav("/"); }} className="text-[#B9C8D5] hover:text-red-300 text-sm flex items-center gap-1" data-testid="account-logout"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>
        <p className="eyebrow text-[#FFD166]">My Account</p>
        <h1 className="font-serif text-5xl md:text-6xl mt-2 text-[#F6F1E4]">Welcome, {user.name?.split(" ")[0] || "friend"}.</h1>
        <p className="text-[#B9C8D5] mt-3">Manage your enchanted bookings, refund eligibility, and past rentals.</p>

        <div className="mt-12">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-5">Upcoming</h2>
          {rows === null ? (
            <div className="text-[#B9C8D5]">Loading bookings…</div>
          ) : groupedUpcoming.length === 0 ? (
            <div className="border border-dashed border-[#FFD166]/25 rounded-2xl p-10 text-center text-[#B9C8D5]" data-testid="account-empty">
              You don't have any upcoming bookings yet.<br />
              <button onClick={() => nav("/")} className="mt-4 btn-ghost">Browse the catalog</button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="account-upcoming">{groupedUpcoming.map((r) => <Row key={r.id} r={r} />)}</div>
          )}
        </div>

        {groupedPast.length > 0 && (
          <div className="mt-14">
            <h2 className="font-serif text-3xl text-[#F6F1E4] mb-5">Past & Cancelled</h2>
            <div className="space-y-4 opacity-80">{groupedPast.map((r) => <Row key={r.id} r={r} />)}</div>
          </div>
        )}

        <div className="mt-14 p-5 rounded-2xl border border-[#FFD166]/25 bg-black/25 text-sm text-[#B9C8D5]" data-testid="refund-policy">
          <p className="text-[#FFD166] font-medium mb-2">Cancellation &amp; refund policy</p>
          <ul className="space-y-1">
            <li>• <strong className="text-[#FFD166]">With Enchanted Peace of Mind (+20 %)</strong> — cancel any time before delivery for a 100 % rental refund. The 20 % PoM fee and credit-card processing fees are non-refundable.</li>
            <li>• <strong className="text-[#F6F1E4]">Standard: 15+ days before delivery</strong> — 100 % refund (minus credit-card processing fees).</li>
            <li>• <strong className="text-[#F6F1E4]">Standard: 7-14 days before delivery</strong> — 50 % refund.</li>
            <li>• <strong className="text-[#F6F1E4]">Standard: less than 7 days</strong> — non-refundable.</li>
            <li>• <strong className="text-[#F6F1E4]">Once your reservation starts</strong> — no refunds available under any tier (rescheduling only).</li>
            <li>• Enchanted Care is refunded along with your booking if you cancel before your gear is delivered.</li>
            <li>• <strong className="text-emerald-300">Tip</strong> — fully refundable if you cancel before delivery day, non-refundable once delivery starts.</li>
          </ul>
        </div>
      </div>

      <CancelModal open={!!cancelling} onClose={() => setCancelling(null)} reservation={cancelling} onConfirmed={() => { setCancelling(null); load(); }} />
      <EditDatesModal open={!!editingDates} onClose={() => setEditingDates(null)} reservation={editingDates} onSaved={() => { setEditingDates(null); load(); }} />
      <AddItemsModal open={!!addingItems} onClose={() => setAddingItems(null)} reservation={addingItems} onSaved={() => { setAddingItems(null); load(); }} />
      <RemoveItemsModal open={!!removingItems} onClose={() => setRemovingItems(null)} reservation={removingItems} onSubmitted={() => { setRemovingItems(null); load(); }} />
    </div>
  );
};
